import {
  makeApiProvider,
  useSuspenseApiQuery,
} from '@pflegenavi/shared-frontend/platform';
import { createContext } from 'react';
import type { IServiceProviderApi } from './api';
import { ServiceProviderApi } from './api';
import type {
  UseMutationResult,
  UseSuspenseQueryOptions,
  UseSuspenseQueryResult,
} from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  ServiceProviderControllerBodyTypes,
  ServiceProviderControllerResultTypes,
} from '@pflegenavi/service-provider-shared/api-endpoints';

const ApiContext = createContext<IServiceProviderApi | undefined>(undefined);
const {
  useApi: useServiceProviderApi,
  ApiProvider: ServiceProviderApiProvider,
} = makeApiProvider({
  name: 'ServiceProvider',
  ApiContext,
  newApi: (tenantId, auth, apiUrl) =>
    new ServiceProviderApi(tenantId, auth, apiUrl),
});

export { ServiceProviderApiProvider, ApiContext as ServiceProviderApiContext };

const SERVICE_PROVIDER_KEY = (globalServiceProviderId: string | undefined) => [
  `service-provider`,
  ...(globalServiceProviderId ? [globalServiceProviderId] : []),
];

export const useServiceProvider = (
  params: {
    globalServiceProviderId: string | undefined;
  },
  options?: Omit<
    UseSuspenseQueryOptions<
      ServiceProviderControllerResultTypes['getServiceProvider'],
      unknown
    >,
    'queryFn' | 'queryKey'
  >
): UseSuspenseQueryResult<
  ServiceProviderControllerResultTypes['getServiceProvider'],
  unknown
> => {
  return useSuspenseApiQuery(
    // eslint-disable-next-line react-hooks/rules-of-hooks
    () => useServiceProviderApi(),
    SERVICE_PROVIDER_KEY(params.globalServiceProviderId),
    (api) => {
      if (params.globalServiceProviderId === undefined) {
        throw new Error('globalServiceProviderId is required');
      }
      return api.getServiceProvider({
        params: {
          globalServiceProviderId: params.globalServiceProviderId,
        },
      }) as Promise<ServiceProviderControllerResultTypes['getServiceProvider']>;
    },
    {
      ...options,
    }
  );
};

export const useUpdateServiceProvider = (): UseMutationResult<
  ServiceProviderControllerResultTypes['updateServiceProvider'],
  unknown,
  {
    globalServiceProviderId: string;
    data: ServiceProviderControllerBodyTypes['updateServiceProvider'];
  }
> => {
  const api = useServiceProviderApi();
  const queryClient = useQueryClient();

  return useMutation<
    ServiceProviderControllerResultTypes['updateServiceProvider'],
    unknown,
    {
      globalServiceProviderId: string;
      data: ServiceProviderControllerBodyTypes['updateServiceProvider'];
    }
  >({
    mutationKey: ['update_service_provider'],
    mutationFn: (data: {
      globalServiceProviderId: string;
      data: ServiceProviderControllerBodyTypes['updateServiceProvider'];
    }) =>
      api?.updateServiceProvider({
        params: { globalServiceProviderId: data.globalServiceProviderId },
        body: data.data,
      }) as Promise<
        ServiceProviderControllerResultTypes['updateServiceProvider']
      >,
    onSuccess: async (data, props) => {
      return await queryClient.invalidateQueries({
        queryKey: SERVICE_PROVIDER_KEY(props.globalServiceProviderId),
      });
    },
  });
};
